import { Injectable } from '@angular/core';

export interface HideableColumnState {
  hidden: boolean;
}

/**
 * Clarity's `[clrDgHideableColumn]` setter calls `ColumnsService.emitStateChange()`
 * unconditionally, without comparing the previous value. Binding an inline object
 * literal (`{ hidden: column.id | hideIDColumns: hides }`) therefore creates a new
 * reference on every change detection cycle, so Clarity re-emits the state of every
 * column all the time and each `clr-dg-cell` recalculates its layout. On wide grids
 * that turns a single column reorder into seconds of blocked UI.
 *
 * This cache hands out a stable object per column so the setter only runs when the
 * hidden flag really changed. Provide it per component - the cache is grid scoped.
 */
@Injectable()
export class HideableColumnStateService {
  private readonly states = new Map<string, HideableColumnState>();
  private hiddenIds = new Set<string>();
  private lastHides: readonly string[] | null = null;

  /**
   * `hides` is the single source of truth. Applying an additional hardcoded blocklist here
   * would make columns such as `ostammid` unreachable from "Spalten verwalten": the user
   * could tick them without any effect. Defaults belong into the initial `hides` value.
   */
  stateFor(columnId: string, hides: readonly string[]): HideableColumnState {
    if (hides !== this.lastHides) {
      this.lastHides = hides;
      this.hiddenIds = new Set(hides.map(id => id?.toLowerCase()));
    }

    const hidden = this.hiddenIds.has(columnId?.toLowerCase());

    const cached = this.states.get(columnId);
    if (cached && cached.hidden === hidden) return cached;

    const state: HideableColumnState = { hidden };
    this.states.set(columnId, state);
    return state;
  }
}
